import _ from 'underscore';
import { PureKnockoutView } from '@bingads-webui/knockout-view';
import 'highcharts';
import { BidLandscapeBaseViewModel } from './base-viewmodel';
import highchartsConfig from './highcharts-config';

function percentileCeil(original) {
  const floor = Math.floor(original * 100) / 100;

  return floor + 0.01;
}

/**
 * @fires update
 * @fires input
 * @fires apply
 * @fires cancel
 */
export class BidLandscapeBaseView extends PureKnockoutView {
  get events() {
    return {
      'click .btn-primary': 'onApply',
      'click .btn-default': 'onCancel',
      'click .closeimage': 'onCancel',
    };
  }

  /**
 * Initialize
 *
 * @param {object} params the option.
 * @param {object} params.entityType - the entity type of the entity
 * @param {object} params.parentItem - the enity info which showing the lanscape for
 * @param {String} params.parentItem.entityName - entityName of the entity
 * @param {String} params.parentItem.Id - the id of the entity
 * @param {boolean} params.parentItem.isDSA - whether belong to a DSA
 * @param {String} params.parentItem.campaignType - campaign's type
 * @param {integer} params.parentItem.SuggestedNewValue - suggested bid value
 *   will mark as (suggested) at landscape graph, and selected by default
 *   if landscape points contains this bid value
 * @param {integer} params.parentItem.CurrentValue - current bid value,
 *   will mark as (current) at landscape graph, and selected when no suggested point
 *   if landscape points contains this bid value
 * @param {boolean} params.inSimpleMode - whether to show it in simple mode
 *   style in simple mode will be different
 * @param {object} params.dataToShow - the dataToShow for bid landscape,
 *  different type of bid landscape may have different data structure
 * @param {object} params.i18n - the localization support for bid landscape
 * @param {object} params.i18n.getString - function to get localized string
 * @param {object} params.i18n.formatDecimalToInteger - format decimal to localized integer
 * @param {object} params.i18n.formatDecimalToFixed - format decimal to 2 digits localized integer
 * @param {object} params.i18n.parseDecimal - convert localized number to decimal
 * @param {object} params.i18n.isValidDecimal - return whether string is valid decimal
 * @return {Void} void
 */
  initialize({
    parentItem = {
      entityName: '',
      Id: 0,
      SuggestedNewValue: -1,
      CurrentValue: -1,
      isDSA: false,
      campaignType: 'Default',
    },
    inSimpleMode = false,
    dataToShow = {},
    enityType = '',
    i18n = { getString: _.identity },
    isForVNext = false,
  }) {
    this.options = {
      parentItem,
      i18n,
      inSimpleMode,
      enityType,
      dataToShow,
    };

    super.initialize(this.options);

    this.isForVNext = isForVNext;
    this.slideChartOptions = null;
    this.viewModel.valueToApply.subscribe((value) => {
      /**
       * update event.
       *
       * @event BidLandscape#update
       * @type {object}
       * @property {?string} valueToApply - the bid value to apply.
       * @property {boolean} inputValid - the bid is valid or not.
       */
      this.trigger('update', {
        valueToApply: value,
        inputValid: this.inputValid(),
      });

      if (this.viewModel.currentSelectedId() === this.viewModel.landscapePointsLength()) {
        /**
         * user input event.
         *
         * @event BidLandscape#input
         * @type {string}
         */
        this.trigger('input', this.viewModel.userInput());
      }
    });

    this.viewModel.currentSelectedId.subscribe(() => {
      this.onSelectionUpdate();
    });
    this.initBidLandscapeChartsOptions();

    this.on('rendered', () => {
      // trigger highcharts render for the first time
      this.renderSlideChart();
    });
  }

  get ViewModel() {
    return BidLandscapeBaseViewModel;
  }

  /**
   * Need extra chart series preparation or not, if need, this is an series data array.
   * @abstract
   * @returns {?array} - extra chart series data.
   */
  prepareExtraSeries() {
    return null;
  }

  onApply() {
    /**
     * apply event.
     *
     * @event BidLandscape#apply.
     */
    this.trigger('apply');
  }

  onCancel() {
    /**
     * cancel event.
     *
     * @event BidLandscape#cancel.
     */
    this.trigger('cancel');
  }

  /**
   * Mark input value as invalid, to stop the apply action
   *   provided for user to apply customized logic checking on input value
   *   for example, mark input as invalid when it's too big for specific senario
   * @param {String} msg - invalid message to show in graph
   *  input will be mark as invalid when it is not null
   * @returns {void}
   */
  markInputValueInvalid(msg) {
    this.viewModel.inputErrorMessage(msg);
  }

  close() {
    this.remove();
  }

  // get table index for specific highcharts point
  getTableKpiIndex4Point(data, clickEvent) {
    const kpiIndexInSortOrder = clickEvent && clickEvent === 'true' ? data.index : data.point.index;

    return this.viewModel.landscapePointsLength() - 1 - kpiIndexInSortOrder;
  }

  // event handler when user selection (on graph or table) update
  // it will refresh the graph and updated the table selection accordingly
  onSelectionUpdate() {
    // trigger keydown each time when select custom input row
    if (this.viewModel.currentSelectedId() >= this.viewModel.landscapePointsLength()) {
      this.$('.bid-landscape__input').trigger('keydown');
    }

    this.renderSlideChart();
  }

  // render highcharts
  renderSlideChart() {
    this.$('.bidlandscape-chart').highcharts(this.prepareChartData());
  }

  // Refresh table when point clicked in highchart graph
  landscapePointClicked(event, pointClicked) {
    const tableKPiIndex = this.getTableKpiIndex4Point(pointClicked, 'true');

    if (_.isNumber(tableKPiIndex)) {
      this.viewModel.currentSelectedId(tableKPiIndex);
    }
  }

  // intialize for the high charts
  initBidLandscapeChartsOptions(options) {
    this.slideChartOptions = options ||
      highchartsConfig.getBidlandscapeChartOptions(
        this.viewModel.isClickGraph(),
        this.options.i18n
      );

    const self = this;

    // Need this to be point clicked in this function, let self be the view instance
    this.slideChartOptions.plotOptions.series.point.events.click = function pointClicked(event) {
      event.stopImmediatePropagation();
      self.landscapePointClicked(event, this);
    };
  }

  // Extract original logic here for children to override
  prepareChartTitles() {
    // set chart xAxis/yAxis title by graph click/impression type
    if (this.viewModel.isClickGraph()) {
      this.slideChartOptions.xAxis.title.text = this.options.i18n.getString('KPI_EstClick').toUpperCase();
      this.slideChartOptions.yAxis.title.text = this.options.i18n.getString('KPI_EstCost').toUpperCase();
    } else {
      this.slideChartOptions.xAxis.title.text = this.viewModel.tableBidText().toUpperCase();
      this.slideChartOptions.yAxis.title.text = this.options.i18n.getString('KPI_EstImp').toUpperCase();
    }
  }

  // Extract original logic here for children to override
  convertPoint(point, isCampaign = false, selectedCol = 'clicks') {
    return {
      bid: point.bidStr,
      x: this.viewModel.isClickGraph() || isCampaign ? point[selectedCol] : point.bid,
      y: this.viewModel.isClickGraph() || isCampaign ? point.cost : point.impressions,
      yStr: this.viewModel.isClickGraph() || isCampaign ? point.costStr : point.impressionsStr,
    };
  }

  // prepare data for chart to render
  prepareChartData(isCampaign = false, selectedCol = 'clicks', extraPoint = {}) {
    if (!this.viewModel.hasData()) {
      // let the viewmodel finish convert logic when there is no data
      this.viewModel.convertDataForBidLandscape();
    }

    // draw chart
    this.prepareChartTitles();

    let maxX = 0;
    let maxY = 0;
    let minX = -1;
    let minY = -1;
    let graphPoint;

    // prepare chart series data
    const seriesData = _.map(this.viewModel.dataArray(), (point, index) => {
      if (point.bid > 0 || isCampaign) {
        // for click graph, showing clicks/clost
        // for impression graph, showing bid/impressions
        graphPoint = {
          ...this.convertPoint(point, isCampaign, selectedCol),
          index, // index in tableData
          ...extraPoint,
        };

        // maintain the min & max for x and y axis
        maxX = Math.max(maxX, graphPoint.x);
        maxY = Math.max(maxY, graphPoint.y);
        minX = minX > 0 ? Math.min(minX, graphPoint.x) : graphPoint.x;
        minY = minY > 0 ? Math.min(minY, graphPoint.y) : graphPoint.y;

        // mark the selected point
        if (index === this.viewModel.currentSelectedId()) {
          graphPoint = _.extend(graphPoint, highchartsConfig.getMakerSetting(isCampaign));
        }
      }
      return graphPoint;
    });

    this.slideChartOptions.series = [{
      name: this.slideChartOptions.xAxis.title.text,
      data: seriesData.reverse(),
    }];

    // if there are extra seris, prepare it
    const extraSeries = this.prepareExtraSeries();

    if (_.isObject(extraSeries)) {
      this.slideChartOptions.series.push({
        data: extraSeries,
      });
    }

    this.slideChartOptions.xAxis.min = minX;
    this.slideChartOptions.yAxis.min = minY;
    this.slideChartOptions.xAxis.max = percentileCeil(maxX);
    this.slideChartOptions.yAxis.max = percentileCeil(maxY);

    return this.slideChartOptions;
  }

  /**
   * Return whether landscape graph has data
   * @returns {boolean} true when has data
   */
  hasData() {
    return this.viewModel.hasData();
  }

  /**
   * Get the value that will be used in apply
   * it is either point in graph or custom input one
   * @returns {integer} value to apply
   */
  valueToApply() {
    return this.viewModel.valueToApply();
  }

  /**
   * Get whether input is valid
   * it can be invalid either by no integer or negetive input
   *  or invalid by user using markInputValueInvalid
   * @returns {boolean} rue when valid
   */
  inputValid() {
    return this.viewModel.inputValid();
  }
}
